import { CheckIcon, Grid, GridRow } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { niceDate } from 'utils/dateCalculations';
import { BpCard } from '../../../components/BpCard/BpCard';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { BpTipTap } from '../../../components/BpTipTap/BpTipTap';
import { BpTipTapText } from '../../../components/BpTipTapText/BpTipTapText';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';

const UpdateOrganizationMutationDocument = graphql(`
  mutation UpdateOrganization($update: OrganizationUpdateInput!, $where: OrganizationWhere!) {
    updateOrganizations(update: $update, where: $where) {
      organizations {
        uuid
        __typename
        displayName
        aboutInfo
        aboutInfoLastUpdated
      }
    }
  }
`);

const InstitutionOverviewSubpageQueryDocument = graphql(`
  query InstitutionOverviewSubpageData($organizationUuid: ID!) {
    organizations(where: { uuid: $organizationUuid }) {
      uuid
      __typename
      aboutInfo
      aboutInfoLastUpdated
    }
  }
`);

export const InstitutionOverviewSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: InstitutionOverviewSubpageQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
    },
  });

  const perms = usePermissionChecker();

  const [, updateOrganization] = useMutation(UpdateOrganizationMutationDocument);

  const [updating, setUpdating] = useState<boolean>(false);
  const [aboutInfo, setAboutInfo] = useState<string>('');

  async function saveAboutInfo() {
    setUpdating(true);
    await updateOrganization({
      where: { uuid: organizationUuid },
      update: { aboutInfo: aboutInfo, aboutInfoLastUpdated: new Date() },
    });
    setUpdating(false);
  }

  const organization = data?.organizations.find((o) => (o.uuid = organizationUuid));
  const canEditAboutInfo = perms?.isOrganizationAdmin(pimAuthClaims.getOrganization());

  return (
    <BpSubpage>
      <Grid>
        <GridRow>
          <BpCard
            header={{
              headline: t('institution.administration'),
              actions: canEditAboutInfo
                ? [
                    {
                      text: t('common.save'),
                      icon: <CheckIcon />,
                      callback: saveAboutInfo,
                      disabled: updating,
                    },
                  ]
                : undefined,
              subHeadline: organization?.aboutInfoLastUpdated
                ? t('common.lastUpdatedAt', {
                    updated: niceDate(organization.aboutInfoLastUpdated, 'medium', 'short'),
                  })
                : undefined,
            }}
          >
            {canEditAboutInfo ? (
              <div>
                <BpTipTap loading={updating} onChange={setAboutInfo} defaultValue={organization?.aboutInfo ?? ''} />
              </div>
            ) : (
              <BpTipTapText content={organization?.aboutInfo ?? ''} />
            )}
          </BpCard>
        </GridRow>
      </Grid>
    </BpSubpage>
  );
};
